import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckSquare2,
  Clock3,
  Headphones,
  Loader2,
  RotateCcw,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import SpacingBox from "./Spacing";
import useFetchData from "../fetchpostData";
import AudioPlayer from "./AudioPlayer";
import Notification from "./Notification";
import ScoreBoxForDoubleAnswer from "./ResponseBoxDoubleType";
import MCQMultiSelect from "./MultiOptionSelect";
import ScoreCardForReading from "./ScoreBoxForReadingSection";
import Timers from "../PageComponent/Timers";
import Timer from "../MockTest/Timer";

const ListeningShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

export default function McqMultipleAnswersPage() {
  const { title } = useParams();

  const section = "mcqdoubleanswer";
  const practiceItemId = title;

  const post = useFetchData(section, title);
  const [originalWords, setOriginalWords] = useState("");
  const [Mcq_Question, setMcq_Question] = useState("");
  const [Title, setTitle] = useState("");
  const [McqOptions, setMcqOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [audioLink, setaudioLink] = useState("");

  useEffect(() => {
    console.log("Post Data :", post);

    if (post && post.media_link) {
      setOriginalWords(post.question);
      setTitle(post.q_title);
      setMcqOptions(post.option);
      setMcq_Question(post.question_mcq);

      const fileName = encodeURIComponent(post.media_link.split("/").pop());

      fetch(`/ptedata/ptemedia/${fileName}`, { credentials: "include" })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setaudioLink(data.url);
          } else {
            console.error("Failed to fetch image URL");
          }
        })
        .catch((error) => console.error("Error fetching signed URL:", error));
    }
  }, [post]);

  console.log("Audio Link:", audioLink);
  console.log("Original Word:", originalWords);

  const handleSelectedOptionsChange = useCallback((selected) => {
    console.log("Selected options:", selected);
    setSelectedOptions(selected);
  }, []);

  useEffect(() => {
    console.log("selectedOptions state updated:", selectedOptions);
  }, [selectedOptions]);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-mcqdoubleanswer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const handleFormSubmission = useCallback(async () => {
    const practiceType = section;

    const practiceData = {
      practiceType,
      practiceItemId,
      response: {
        questionId: practiceItemId,
        answer: selectedOptions,
        isAttempted,
      },
    };

    try {
      const result = await savePractice(practiceData);
      setMessage("Practice data saved successfully!");
      console.log("practiceData:", practiceData);

      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
      }, 2000);
    } catch (error) {
      setMessage("Failed to save practice data.");
      setNotificationType("error");
      setShowNotification(true);
      setLoading(false);
    }
  }, [selectedOptions, isAttempted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitted(true);

    handleFormSubmission();
  };

  const handleRetake = () => {
    setSelectedOptions([]);
    setSubmitted(false);
    setIsAttempted(false);
    setSavedPractice(null);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleIsAttempted = (isAttemptedValue) => {
    setIsAttempted(isAttemptedValue);
  };

  const attemptedStatus = isAttempted ? "Attempted" : "Not Attempted";

  console.log("savedPractice", savedPractice);

  return (
    <ListeningShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Listening practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {Title || "Multiple-choice, choose multiple answers"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Listen to the recording and select all correct responses.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {attemptedStatus}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Choose all correct answers
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Listen to the recording and answer the question by selecting
                  all correct responses. You will need to select more than one
                  response.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Audio
                </p>
                <h2 className="text-lg font-extrabold text-gray-900">
                  Recording playback
                </h2>
              </div>
            </div>
            {audioLink && <AudioPlayer audioSrc={audioLink} />}
          </section>

          <section className="space-y-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Answer window
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Select your responses
                </h2>
              </div>
              <span className="inline-flex min-h-10 items-center rounded-md bg-purple-50 px-3 py-2 text-sm font-bold text-purple-800 ring-1 ring-purple-100">
                <Timer
                  initialMinutes={10}
                  initialSeconds={0}
                  Text="Time remaining"
                />
              </span>
            </div>

            <MCQMultiSelect
              question={Mcq_Question}
              options={McqOptions}
              onOptionSelect={handleSelectedOptionsChange}
              selectedOption={selectedOptions}
              submitted={submitted}
            />
          </section>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit when you have selected all correct responses.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleSubmit}
                disabled={submitted}
              >
                {loading ? (
                  <>
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Submit
                  </>
                )}
              </button>

              {submitted && (
                <button
                  type="button"
                  onClick={handleRetake}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-slate-100 px-5 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Retake
                </button>
              )}
            </div>
          </section>

          {submitted ? (
            <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-green-50 text-green-700">
                  <CheckSquare2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Result
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Latest score
                  </h2>
                </div>
              </div>
              <div className="flex justify-center py-6">
                <ScoreCardForReading
                  score={savedPractice?.totalScore}
                  maxScore={savedPractice?.correctOptions}
                />
              </div>
            </section>
          ) : null}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Treat each option separately. Mark it only if it is directly
                  supported by what you heard.
                </p>
              </div>
            </div>
          </section>

          <ScoreBoxForDoubleAnswer
            originalWords={originalWords}
            practiceItemId={practiceItemId}
            onIsAttempted={handleIsAttempted}
          />
        </aside>
      </div>

      <SpacingBox />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={handleCloseNotification}
        />
      )}
    </ListeningShell>
  );
}
